import { For, Show, createMemo } from 'solid-js';
import { bakeLinkedPositions, type ObjectContext } from './objects';
import type { SelectionContext } from '../canvas/selection';
import { textRendersEmpty } from '../canvas/text/BaseTextObject';
import { Object } from '../canvas/Object';
import { ImageDropper } from '../canvas/images/ImageDropper';
import { geometrySettled, onGeometrySettled } from '../canvas/text/fitText';
import type { RuntimeObject } from '../types';
import type { ThemeRecord } from './themeVariables';
import { resolveThemeVar } from './themeVariables';
import type { DesignLinkContext } from './designLink';
import type { HistoryContext } from './history';
import { ICanvasPane } from 'Shared/posters/canvas/CanvasWrap';
import { reconcile, unwrap } from 'solid-js/store';
import { hasNestedObjects, hoistDesign } from 'Shared/posters/project/flatten';
import { InputHandler } from 'Shared/posters/canvas/InputHandler';
import { AutoSave } from './AutoSave';
import { isUserFacing } from './PosterMode';

export function EditablePosterSvg(P: ICanvasPane)
{
	const canvasCtx = P.canvas;
	const selCtx    = P.selection;
	const objCtx    = P.objects;
	const dragCtx   = P.drag;
	/* Incorporates a signal */
	const imageDropper = new ImageDropper(objCtx, selCtx, canvasCtx, dragCtx, P.history, P.upload);

	// In a user-facing context a top-level text box that renders to only whitespace is omitted
	// entirely (no diagonal-stripe placeholder) — the same drop a flex container applies to its
	// empty children, generalised to the canvas. Design mode keeps every object visible.
	const visibleObjects = createMemo<RuntimeObject[]>(() =>
		isUserFacing(P.mode) ? objCtx.objects().filter(obj => !textRendersEmpty(obj, P.record)) : objCtx.objects()
	);

	// A text object is measured in the DOM, so it has to be rendered before its box is known: it mounts
	// on its unmeasured seed and the fits land over the next frames, reflowing every container they sit
	// in. In design mode that settling is the designer's own edit landing, but entering user mode
	// remounts every text object at once, so the whole poster visibly shuffles into place. Keep the
	// object layer laid out but hidden until the fits have settled — visibility, not display, so getBBox
	// still measures. (The flatten itself needs no waiting: UserViewFlattener hoists in setup, so the
	// objects below mount flat. What lands on the settle is the linked layout's measured positions.)
	//
	// Latched: this is a first-render concern, so once the layer has been shown it stays shown. The
	// fit-settled gate is module-global (fitText's pending counter), shared by every poster mounted on
	// the page — including the event page's hidden capture poster, which remounts (and so re-fits) all
	// of its text on any edit to the record. Re-hiding on those would blank the poster the user is
	// dragging in. A fresh nested design re-arms the latch: it needs hoisting before it's fit to show.
	const objectsVisible = createMemo<boolean>(prev => {
		if (P.mode != 'user')
			return true;
		if (hasNestedObjects(objCtx.objects()))
			return false;
		return prev || geometrySettled();
	}, false);

	// Canvas background: a theme-variable binding replaces the static colour at render time.
	const bgFill = createMemo(() => {
		const v = resolveThemeVar(P.theme, canvasCtx.canvasBindings().background);
		return v !== undefined ? String(v) : canvasCtx.canvasBg();
	});

	const svgStyle = {
		display:          'block',
		'box-shadow':     '0 25px 70px rgba(0,0,0,.8), 0 0 0 1px rgba(255,255,255,.06)',
		'border-radius':  '2px',
		outline:          imageDropper.isDragOver() ? '2px dashed #4d9eff' : 'none',
		'outline-offset': '3px',
		margin: '0 auto',
		'user-select': 'none'
	};

//TODO InputHandler doesn't have to be a component. Convert.	
//TODO AutoSave doesn't have to be a component. Convert.	

	return (<>
		<InputHandler objects={P.objects} selection={P.selection} drag={P.drag} history={P.history} />

		<AutoSave objects={P.objects} selection={P.selection} canvas={P.canvas} history={P.history} posterId={P.posterId!}
			save={P.save} mode={P.mode} design={P.design}
		/>

		<svg
			ref={el => dragCtx.setSvgRef(el)}
			data-keeps-selection=''
			width={canvasCtx.canvas().width}
			height={canvasCtx.canvas().height}
			viewBox={`0 0 ${canvasCtx.canvas().width} ${canvasCtx.canvas().height}`}
			overflow='hidden'
			style={svgStyle}
			onDragEnter={e => imageDropper.onDragEnter(e)}
			onDragOver={e => imageDropper.onDragOver(e)}
			onDragLeave={e => imageDropper.onDragLeave()}
			onDrop={e => imageDropper.onDrop(e)}
		>
			{/* Background fill + deselect target */}
			<rect
				x='0' y='0'
				width={canvasCtx.canvas().width}
				height={canvasCtx.canvas().height}
				fill={bgFill()}
				onMouseDown={e => { e.stopPropagation(); selCtx.deselect() }}
			/>

			<g style={{ visibility: objectsVisible() ? 'visible' : 'hidden' }}>
				{/* Ahead of the object layer so its setup hoists the design before the objects below
					mount — a nested design never reaches the DOM. Keyed on the objects array itself so a
					wholesale design swap gives it a fresh mount; a produce edit (or its own reconcile)
					mutates the array in place and leaves its identity alone, so it can't re-trigger itself. */}
				<Show when={P.mode=='user'}>
					<Show when={objCtx.objects()} keyed>
						<UserViewFlattener objects={objCtx} selection={P.selection} design={P.design} history={P.history} theme={P.theme} />
					</Show>
				</Show>

				<For each={visibleObjects()}>{obj => <Object
					objects={objCtx}
					selection={P.selection}
					history={P.history}
					drag={dragCtx}
					images={P.images}
					upload={P.upload}
					design={P.design}
					record={P.record}
					theme={P.theme}
					textRefs={P.textRefs}
					canvas={canvasCtx.canvas()}
					object={obj}
					mode={P.mode}
				/>}</For>
			</g>

			{/* Always-on-top layer for selection box + resize handles */}
			<g data-handles='' ref={el => dragCtx.setHandlesObject(el)} />
		</svg>
	</>);
}

//FIXME convert UserViewFlattener over to non-component

// ── UserViewFlattener ─────────────────────────────────────────────────────────────

// Keeps user mode on a flat design, in two steps that split cleanly along "needs measurement":
//
//  1. The flatten itself is structural, so it runs *here and now* — synchronously in setup, before the
//     object layer below it mounts, so nothing nested is ever rendered (or, on the event page, saved).
//     hoistDesign works off the spec alone; no bbox, no waiting for a fit to land. The event page has
//     normally hoisted already (at copy time, so the DB never holds a nested design) and this is then a
//     no-op; it does the work for the design page's User Preview and for records saved nested by an
//     earlier build.
//
//  2. The *layout* does need measurement, and it comes from the shadow: the nested template laid out
//     live against the mounted leaves' measured sizes (designLink). The view already renders those
//     positions, so all that is left is persistence — write them into the store once every in-flight
//     fit has landed, and let AutoSave carry them to the DB. onGeometrySettled re-fires whenever a late
//     font or a record change makes the fits re-run, so this tracks the reflow rather than freezing the
//     first pass; it stays quiet while the bake changes nothing.
function UserViewFlattener(P: { objects: ObjectContext, selection: SelectionContext, design: DesignLinkContext, history: HistoryContext, theme: ThemeRecord | undefined })
{
	const objCtx = P.objects;

	/* No relink step: detachments are carried by the flat leaves themselves (see designLink), so a
	   fresh flat view — entering User Preview, or a wholesale design swap on the event page — arrives
	   with whatever detachments belong to it, and a hoisted template arrives with none. */
	if (hasNestedObjects(objCtx.objects())) {
		const flat = hoistDesign(unwrap(objCtx.objects()), P.theme);
		/* A structural container is dropped outright, so a selection made in design mode can't be
		   carried into the flat view. */
		P.selection.deselect();
		objCtx.setObjects(reconcile(flat));
	}

	onGeometrySettled(() => {
		if (!P.design.linked())
			return;
		if (bakeLinkedPositions(objCtx, P.selection.impls, P.design))
			P.history.markDirty();
	});

	return null;
}

